/**
 * @fileoverview Componente raiz da aplicação VikanaWeb.
 * Configura os provedores globais: Redux, Router, Tema e Material-UI.
 * Implementa verificação automática de sessão a cada 1 minuto.
 *
 * @module App
 * @since 1.0.0
 */

import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import Store from './components/Store/Store';
import ResetPassword from './components/Auth/ResetPassword';
import { CustomThemeProvider, useTheme } from './contexts/AccessibilityContext';
import QueryProvider from './QueryProvider';
import { Suspense } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { useSessionCheck } from './hooks/useSessionCheck';
import { usePlatformOptimizedSync } from './hooks/usePlatformOptimizedSync';

import AppInitializer from './components/AppInitializer/AppInitializer';

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
      <CircularProgress />
    </Box>
  );
}

/**
 * App content with theme applied and session check.
 * Verifica sessão automaticamente a cada 1 minuto.
 * Otimiza hooks por plataforma: mobile vs desktop.
 */
function AppContent() {
  const { getTheme } = useTheme();

  // Verificação automática de sessão a cada 1 minuto (todas as plataformas)
  useSessionCheck();

  // Sincronização otimizada por plataforma:
  // - Desktop: useSessionRefresh (cookies/sessão)
  // - Mobile: useMobileSync (localStorage + sync manual)
  usePlatformOptimizedSync();

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path='/reset-password/:token' element={<ResetPassword />} />
          <Route path='/*' element={<Store />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

/**
 * Root application component with all providers
 */
function App() {
  return (
    <Provider store={store}>
      <QueryProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <CustomThemeProvider>
            <AppInitializer>
              <AppContent />
            </AppInitializer>
          </CustomThemeProvider>
        </BrowserRouter>
      </QueryProvider>
    </Provider>
  );
}

export default App;
